/**
 * Wrapper that makes any entity picker row draggable onto the canvas via
 * the canvas-wide `CanvasDndProvider` dnd-kit context.
 *
 * Drag behaviour:
 *   - Activates after 8px pointer movement (matches the explorer's
 *     PointerSensor activation constraint).
 *   - When the row is part of a selection of ≥ 2, the whole selection
 *     is packed into the drag payload (mirrors ExplorerFileRow).
 *   - Otherwise only this row's entity is dragged.
 *
 * Click behaviour:
 *   - Plain click  : clear selection, call onSelect (today's UX).
 *   - Cmd/Ctrl     : toggle this row in the selection.
 *   - Shift        : range-select from the anchor to this row.
 *
 * Visual state:
 *   - Selected rows receive a ring + tinted background.
 *   - Cursor switches to `grab` / `grabbing`.
 */

import { cn } from "@/utils";
import { getEntityName, type Entity } from "@cohesive/ui-components";
import { useDraggable } from "@dnd-kit/core";
import { useCallback, useEffect, type ReactNode } from "react";
import { useEntitySelection } from "./useEntitySelection";

interface EntityDragRowProps {
  entity: Entity;
  /**
   * Generic projection of `entity` (from a `entity_format=both` fetch). This is
   * what lands on the canvas — the typed `entity` drives the row's rich display
   * only. Falls back to `entity` when absent so un-wired pickers keep working.
   */
  generic?: Entity;
  /** Stable, globally-unique id for this entity (e.g. entity.data.id). */
  entityId: string;
  /**
   * Flat ordered list of all entity ids in this picker's result set.
   * Used to compute Shift-click ranges.
   */
  orderedIds: string[];
  /** Called with the entity when the user clicks without a modifier. */
  onSelect: (entity: Entity) => void;
  /**
   * Namespace for the dnd-kit draggable id — avoids collisions when the
   * same entity appears in two open popovers simultaneously. Match the
   * explorer's `dragNamespace` pattern.
   */
  dragScope?: string;
  children: ReactNode;
}

export function EntityDragRow({
  entity,
  generic,
  entityId,
  orderedIds,
  onSelect,
  dragScope = "default",
  children,
}: EntityDragRowProps) {
  const selection = useEntitySelection();
  const isSelected = selection.isSelected(entityId);

  // The generic projection is what gets placed on the canvas (drag payload +
  // plain-click placement); the typed `entity` drives the rich row display only.
  const placed = generic ?? entity;

  // Register this entity in the selection bag so multi-select drags can
  // resolve entity objects for all selected ids without a parent list.
  //
  // Depend only on the two stable callbacks (both created with useCallback
  // and empty deps in EntitySelectionProvider), NOT on `selection` itself.
  // The `selection` object is recreated on every selectedIds change, so
  // including it here would re-register every visible row on every
  // Cmd-click or Shift-click — O(N) unnecessary Map ops per interaction.
  const { registerEntity, unregisterEntity } = selection;
  useEffect(() => {
    registerEntity(entityId, placed);
    return () => unregisterEntity(entityId);
  }, [entityId, placed, registerEntity, unregisterEntity]);

  // Build drag payload: full selection when ≥ 2 rows are selected and
  // this row is part of the selection; otherwise just this one entity.
  // The payload carries generics — only generics may land on the canvas.
  const dragEntities: Entity[] =
    isSelected && selection.selectedIds.size > 1
      ? selection.getSelectedEntities(placed)
      : [placed];

  const primaryName = getEntityName(entity) || entityId;

  const { setNodeRef, listeners, attributes, isDragging } = useDraggable({
    id: `entity-row:${dragScope}:${entityId}`,
    data: {
      kind: "entity",
      entities: dragEntities,
      primaryName,
    },
  });

  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.metaKey || e.ctrlKey) {
        e.preventDefault();
        selection.toggle(entityId);
        return;
      }
      if (e.shiftKey) {
        e.preventDefault();
        selection.rangeSelect(orderedIds, entityId);
        return;
      }
      // Plain click: clear any selection, then add the generic to the canvas.
      // (Wrapper selectors that branch on entity type ignore this argument and
      // resolve the generic from their own id→generic map instead.)
      selection.clear();
      onSelect(placed);
    },
    [placed, entityId, orderedIds, onSelect, selection],
  );

  return (
    <div
      ref={setNodeRef}
      {...listeners}
      {...attributes}
      data-selected={isSelected || undefined}
      onClick={handleClick}
      className={cn(
        "relative cursor-grab active:cursor-grabbing",
        isSelected &&
          [
            // Ring provides the unmistakable selection outline regardless of
            // what background the child card uses (ring is part of the
            // wrapper's own box-model, not covered by children).
            "ring-2 ring-inset ring-rust/65 rounded-sm",
            // ::before overlay renders above child card backgrounds (which
            // are opaque on BaseEntityCard-derived cards and would otherwise
            // hide a plain wrapper `bg-*` tint). pointer-events-none ensures
            // interactions inside the card remain fully usable.
            "before:content-[''] before:absolute before:inset-0 before:z-10",
            "before:bg-primary/10 before:pointer-events-none",
          ].join(" "),
        isDragging && "opacity-50",
      )}
      style={{ userSelect: "none" }}
    >
      {children}
    </div>
  );
}
